/* eslint-disable @typescript-eslint/no-explicit-any */
import { APP_VERSION, ModelStateHttpErrorResponse } from '../../types';
import { AppRoute } from '../../enums';
import { ConfigSettingsService } from '..';
import {
  HttpClient,
  HttpContext,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams,
  HttpStatusCode,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { LogService } from '../log/log.service';
import { Observable, firstValueFrom, of, throwError } from 'rxjs';
import { Router } from '@angular/router';
import { SKIP_MAINTENANCE_INTERCEPTOR } from '../../interceptors';
import { ServerResponse } from '../../interfaces';
import { StaticFileContent } from '../../models';
import { marker as _ } from '@colsen1991/ngx-translate-extract-marker';
import { catchError } from 'rxjs/operators';

interface HttpRequestOptions {
  context?: HttpContext;
  headers?: HttpHeaders;
  params?: HttpParams;
  observe?: 'body' | 'events' | 'response';
  reportProgress?: boolean;
  responseType?: 'json' | 'text' | 'blob' | 'arraybuffer';
  withCredentials?: boolean;
  body?: unknown | null;
}

interface HttpRequestOptionsDelete extends HttpRequestOptions {
  responseType: 'json';
  observe: 'body';
}

@Injectable()
export class ApiService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly configService = inject(ConfigSettingsService);
  private readonly _versionToken = inject(APP_VERSION);

  public get urlApi(): string {
    return `${this.configService.appSettings.connection.res}/api`;
  }

  public delete(endpoint: string, reqOpts?: HttpRequestOptions): Observable<ServerResponse | unknown> {
    return this.http
      .delete(`${this.urlApi}/${endpoint}`, reqOpts as HttpRequestOptionsDelete)
      .pipe(catchError((err) => this.handleApiErrors(err)));
  }

  public deleteUser(password: string): Observable<ServerResponse | unknown> {
    return this.delete('users/me', { body: { password } });
  }

  public doPatch<T>(url: string, body: unknown, reqOpts?: HttpRequestOptions): Observable<T> {
    return this.http
      .patch(url, body, { ...this.mergeOptions(reqOpts) } as any)
      .pipe(catchError((err) => this.handleApiErrors(err))) as Observable<T>;
  }

  public doPost<T>(url: string, body: unknown, reqOpts?: HttpRequestOptions): Observable<T> {
    return this.http
      .post(url, body, { ...this.mergeOptions(reqOpts) } as any)
      .pipe(catchError((err) => this.handleApiErrors(err))) as Observable<T>;
  }

  public getStaticInfoFile(): Observable<StaticFileContent | HttpErrorResponse | unknown> {
    return this.restGet('file/InfoStartseite').pipe();
  }

  public getVersionInfo(): Promise<{ isCompatible: boolean; serverVersion: string }> {
    const context = new HttpContext().set(SKIP_MAINTENANCE_INTERCEPTOR, true);
    return firstValueFrom(
      this.http
        .post<{
          isCompatible: boolean;
          serverVersion: string;
        }>(
          `${this.urlApi}/utils/version`,
          { clientVersion: this._versionToken.version },
          { observe: 'body', responseType: 'json', context },
        )
        .pipe(
          catchError(() =>
            of({
              isCompatible: true,
              serverVersion: '0.0.0',
            }),
          ),
        ),
    );
  }

  public patch<T>(endpoint: string, body: unknown, reqOpts?: HttpRequestOptions): Observable<T> {
    return this.doPatch<T>(`${this.urlApi}/${endpoint}`, body, reqOpts);
  }

  public post<T>(endpoint: string, body: unknown, reqOpts?: HttpRequestOptions): Observable<T> {
    return this.doPost<T>(`${this.urlApi}/${endpoint}`, body, reqOpts);
  }

  public put<T>(endpoint: string, body?: unknown, reqOpts?: HttpRequestOptions): Observable<T | void | unknown> {
    return this.http
      .put<T>(`${this.urlApi}/${endpoint}`, body, this.mergeOptions(reqOpts) as any)
      .pipe(catchError((err) => this.handleApiErrors(err)));
  }

  public restGet<T>(endpoint: string, reqOpts?: HttpRequestOptions): Observable<T> {
    return this.get(`${this.urlApi}/${endpoint}`, reqOpts);
  }

  private get<T>(url: string, reqOpts?: HttpRequestOptions): Observable<T> {
    if (!reqOpts) {
      reqOpts = {
        params: new HttpParams(),
        responseType: 'json',
      };
    }

    return this.http
      .get<T>(url, this.mergeOptions(reqOpts) as any)
      .pipe(catchError((err) => this.handleApiErrors(err))) as Observable<T>;
  }

  private handleApiErrors<T>(err: HttpErrorResponse): Observable<T> {
    try {
      err.error.message = err.error.title ?? err.error.Message ?? err.error.message;
    } catch {
      // do nothing
      LogService.info('HTTP Error handling failed');
    }

    if (!err || err.status < 0) {
      return of(err as HttpErrorResponse as T);
    }

    if (err.status === 0) {
      return throwError(() => ({ message: _('server.connection.noInternetConnection') }));
    }

    if (err.status < HttpStatusCode.Ok) {
      return of(err as HttpErrorResponse as T);
    } else if (err.status < HttpStatusCode.BadRequest) {
      return of(undefined as T);
    } else if (err.status < HttpStatusCode.InternalServerError) {
      return this.handleClientErrorLevel(err) as Observable<T>;
    } else if (err.status >= HttpStatusCode.InternalServerError) {
      return this.handleServerErrorLevel(err) as Observable<T>;
    }

    if (err.error.message) {
      return throwError(() => ({ message: err.error.message }));
    }

    if (!err.error) {
      return throwError(() => ({ message: _('general.text.error') }));
    }

    return of({} as T);
  }

  private handleClientErrorLevel(err: HttpErrorResponse): Observable<HttpErrorResponse> {
    if (err.status === HttpStatusCode.BadRequest) {
      if (!err.error.message) {
        return throwError(() => err.error);
      }

      if (ApiService.isModelStateHttpErrorResponse(err)) {
        const errors = err.error.errors;
        const message = Object.values(errors)[0][0];

        return throwError(() => ({ message }));
      }

      const error = err.error;
      error.message = err.error.message;

      return throwError(() => error);
    }

    if (err.status === HttpStatusCode.Conflict) {
      return throwError(() => ({ message: err.error.message }));
    }

    if (err.status === HttpStatusCode.Unauthorized) {
      this.router.navigate([AppRoute.Logout]);
      return throwError(() => ({ message: 'general.text.session_expired' }));
    }

    if (err.status === HttpStatusCode.NotFound) {
      return throwError(() => ({ message: _('server.connection.404') }));
    }

    if (err.status === HttpStatusCode.UnsupportedMediaType) {
      return throwError(() => err);
    }

    return of(err);
  }

  private handleServerErrorLevel(err: HttpErrorResponse): Observable<HttpErrorResponse> {
    if (err.status === HttpStatusCode.InternalServerError && err.error.message) {
      return throwError(() => ({
        message: err.error.exceptionMessage,
        title: _('general.label.error'),
      }));
    }

    return of(err);
  }

  private mergeOptions(options?: Partial<HttpRequestOptions>): HttpRequestOptions {
    const mergedOptions = {
      responseType: options?.responseType ?? 'json',
      observe: options?.observe ?? 'body',
      context: options?.context ?? new HttpContext(),
      headers: options?.headers ?? new HttpHeaders(),
      params: options?.params ?? new HttpParams(),
      reportProgress: options?.reportProgress ?? false,
      withCredentials: options?.withCredentials ?? false,
      body: options?.body ?? null,
    };
    return mergedOptions;
  }

  private static isModelStateHttpErrorResponse(response: HttpErrorResponse): response is ModelStateHttpErrorResponse {
    if (typeof response.error !== 'object' || !('errors' in response.error)) {
      return false;
    }

    const errors = response.error.errors;
    if (typeof errors !== 'object') {
      return false;
    }

    return Object.values(errors).every((err) => Array.isArray(err));
  }
}
